import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../utils/apiInterceptor';
import { API_BASE_URL } from '../../utils/paths';

// Async thunks for API calls
export const fetchAllSpots = createAsyncThunk(
  'spots/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots`, {
        method: 'GET',
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const fetchSpot = createAsyncThunk(
  'spots/getDetails',
  async (spotId, { rejectWithValue, getState }) => {
    
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/${spotId}`, {
        method: 'GET',
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const createSpot = createAsyncThunk(
  'spots/create',
  async (spotData, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/create`, {
        method: 'POST',
        body: JSON.stringify(spotData),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const updateSpot = createAsyncThunk(
  'spots/update',
  async ({ spotId, spotData }, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/${spotId}/update`, {
        method: 'PUT',
        body: JSON.stringify(spotData),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const deleteSpot = createAsyncThunk(
  'spots/delete',
  async (spotId, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/spots/${spotId}/delete`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        return rejectWithValue(data);
      }

      return spotId; // Return the deleted spot ID
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

const savedSpotsSlice = createSlice({
  name: 'savedSpots',
  initialState: {
    spots: {},
    currentSpot: null,
    isLoading: false,
    isDeleting: false,
    errors: null,
    isUpdating: false,
    isSaving: false,
  },
  reducers: {
    clearErrors: (state) => {
      state.errors = null;
    },
    clearCurrentSpot: (state) => {
      state.currentSpot = null;
    },
    setCurrentSpot: (state, action) => {
      state.currentSpot = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Spots
      .addCase(fetchAllSpots.pending, (state) => {
        state.isLoading = true;
        state.errors = null;
      })
      .addCase(fetchAllSpots.fulfilled, (state, action) => {
        state.isLoading = false;
        // Transform array to object with id as key
        state.spots = action.payload.reduce((acc, spot) => {
          acc[spot.id] = spot;
          return acc;
        }, {});
        state.errors = null;
      })
      .addCase(fetchAllSpots.rejected, (state, action) => {
        state.isLoading = false;
        state.errors = action.payload?.errors || ['Failed to fetch spots'];
      })
      // Fetch Spot by ID
      .addCase(fetchSpot.pending, (state) => {
        state.isLoading = true;
        state.errors = null;
      })
      .addCase(fetchSpot.fulfilled, (state, action) => {
        state.isLoading = false;
        state.spots[action.payload.id] = action.payload;
        state.currentSpot = action.payload;
        state.errors = null;
      })
      .addCase(fetchSpot.rejected, (state, action) => {
        state.isLoading = false;
        state.errors = action.payload?.errors || ['Failed to fetch spot details'];
      })
      // Create Spot
      .addCase(createSpot.pending, (state) => {
        state.errors = null;
        state.isSaving = true;
      })
      .addCase(createSpot.fulfilled, (state, action) => {
        state.spots[action.payload.id] = action.payload;
        state.currentSpot = action.payload;
        state.errors = null;
        state.isSaving = false;
      })
      .addCase(createSpot.rejected, (state, action) => {
        state.errors = action.payload?.errors || ['Failed to create spot'];
        state.isSaving = false;
      })
      // Update Spot
      .addCase(updateSpot.pending, (state) => {
        state.errors = null;
        state.isUpdating = true;
      })
      .addCase(updateSpot.fulfilled, (state, action) => {
        const updatedSpot = action.payload;
        state.spots[updatedSpot.id] = updatedSpot;
        state.currentSpot = updatedSpot;
        state.errors = null;
        state.isUpdating = false;
      })
      .addCase(updateSpot.rejected, (state, action) => {
        state.errors = action.payload?.errors || ['Failed to update spot'];
        state.isUpdating = false;
      })
      // Delete Spot
      .addCase(deleteSpot.pending, (state) => {
        state.isDeleting = true;
        state.errors = null;
      })
      .addCase(deleteSpot.fulfilled, (state, action) => {
        state.isDeleting = false;
        const spotId = action.payload; // payload is just the spotId
        delete state.spots[spotId];
        // Clear current spot if it was the deleted one
        if (state.currentSpot?.id === spotId) {
          state.currentSpot = null;
        }
        state.errors = null;
      })
      .addCase(deleteSpot.rejected, (state, action) => {
        state.isDeleting = false;
        state.errors = action.payload?.errors || ['Failed to delete spot'];
      });
  },
});

export const { clearError, clearCurrentSpot, setCurrentSpot } = savedSpotsSlice.actions;
export default savedSpotsSlice.reducer;